RivalIQ.module('metrics.new.client', function (client, App, Backbone, Marionette, $, _, s) {

    const NUM_TRIES = 3;

    client._fetchMetrics = function (definitions) {
        if (_.isEmpty(definitions)) {
            return $.when([]);
        }

        const sanitized = _.map(definitions, RivalIQ.metrics.new.MetricRequest.serialize);
        const options = {
            contentType: 'application/json',
            data: JSON.stringify(sanitized),
            type: 'POST',
            url: '/api/new/metrics'
        };

        return $.ajax(options);
    };

    const CacheLayer = Marionette.Object.extend({

        // debug methods
        printCurrentState: function () {
            const me = this;

            RivalIQ.logger.debug('pending requests:', _.keys(me.pendingRequestsByUUID));
            RivalIQ.logger.debug('inflight requests:', _.keys(me.inflightRequestsByUUID));
            RivalIQ.logger.debug('cached requests:', _.keys(me.cachedResultsByUUID));
            RivalIQ.logger.debug(`${_.size(me.waitingCallers)} waiting callers`);
        },

        initialize: function () {
            const me = this;

            me.resetInvalidationId();
            me.pendingRequestsByUUID = {};
            me.cachedResultsByUUID = {};
            me.inflightRequestsByUUID = {};

            // an array of objects that contain a deferred and an array of UUIDs of requests to resolve.
            me.waitingCallers = [];

            RivalIQ.data.CacheLayer.register(me);

            RivalIQ.on('reset:userSettings', () => {
                // Listening recommendations are dependent on the last search a user fired, so we need to invalid if those change.
                _.each(me.cachedResultsByUUID, (result, uuid) => {
                    if (result.request.dataSourceId === RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH_RECOMMENDATIONS) {
                        delete me.cachedResultsByUUID[uuid];
                    }
                });
            });
        },

        // The invalidationId is changed whenever the cache is invalidated; that way, we can see if
        // the cache was invalidated while this request was inflight and, if so, ignore the results.
        resetInvalidationId: function () {
            const me = this;

            me._invalidationId = _.uniqueId(me.name);
        },

        // returns a promise that resolves to an array of results for the given metricRequests
        fetch: function (metricRequests) {
            const me = this;

            if (!_.isArray(metricRequests)) {
                metricRequests = [metricRequests];
            }

            const canonicalized = _.map(metricRequests, metricRequest => {
                return RivalIQ.metrics.new.MetricRequest.canonicalizeMetricRequest(metricRequest);
            });

            _.each(canonicalized, metricRequest => {
                const uuid = RivalIQ.metrics.new.MetricRequest.uniqueKeyForMetricRequest(metricRequest);
                if (!me.cachedResultsByUUID[uuid] && !me.inflightRequestsByUUID[uuid]) {
                    me.pendingRequestsByUUID[uuid] = metricRequest;
                }
            });

            const deferred = new $.Deferred();
            const caller = {
                deferred: deferred,
                canonicalized: canonicalized
            };
            me.waitingCallers.push(caller);

            me._notifyWaitingCallers();

            return deferred;
        },

        _notifyWaitingCallers: function () {
            const me = this;

            _.each(me.waitingCallers, (waitingCaller, idx) => {
                // waitingCaller can be null in some scenarios where we have nulled it out but haven't hit the compact call yet
                // (which can happen if the client issues a cached request in response to a request being resolved).
                if (!waitingCaller) {
                    return;
                }
                let hasAllResults = true;
                const results = _.map(waitingCaller.canonicalized, request => {
                    const uuid = RivalIQ.metrics.new.MetricRequest.uniqueKeyForMetricRequest(request);

                    const cachedResponse = me.cachedResultsByUUID[uuid];
                    if (cachedResponse) {
                        return cachedResponse;
                    } else {
                        hasAllResults = false;
                    }
                });

                if (hasAllResults) {
                    me.waitingCallers[idx] = null;
                    waitingCaller.deferred.resolve(results);
                }
            });

            me.waitingCallers = _.compact(me.waitingCallers);

            if (!RivalIQ.data.CacheLayer.isBuffering()) {
                me._debouncedFlush();
            }
        },

        _debouncedFlush: _.debounce(function () {
            const me = this;
            me.flush();
        }, 100),

        _requestMetricsForGroupAsync: async function (requests) {
            const me = this;

            const requestInvalidationId = me._invalidationId;

            for (let tryNum = 0; tryNum < NUM_TRIES; tryNum++) {
                try {
                    const results = await client._fetchMetrics(requests);

                    if (requestInvalidationId !== me._invalidationId) {
                        return;
                    }
                    _.each(results, (result, idx) => {
                        const uuid = RivalIQ.metrics.new.MetricRequest.uniqueKeyForMetricRequest(requests[idx]);
                        me.cachedResultsByUUID[uuid] = _.extend({}, result, {
                            request: requests[idx]
                        });
                        delete me.inflightRequestsByUUID[uuid];
                    });

                    me._notifyWaitingCallers();
                    break;

                } catch (error) {
                    if (requestInvalidationId !== me._invalidationId) {
                        return;
                    }

                    const isLastAttempt = tryNum === NUM_TRIES - 1;
                    if (!isLastAttempt) {
                        RivalIQ.logger.warn('Error fetching metrics, retrying', error);
                        continue;
                    }

                    const errorResponse = {
                        error: error.responseJSON || error.responseText || error
                    };
                    _.each(requests, (request, idx) => {
                        const uuid = RivalIQ.metrics.new.MetricRequest.uniqueKeyForMetricRequest(request);
                        me.cachedResultsByUUID[uuid] = _.extend({}, errorResponse, {
                            request: request
                        });

                        delete me.inflightRequestsByUUID[uuid];
                    });

                    me._notifyWaitingCallers();

                    RivalIQ.execute('cacheLayer:load:fail', error);
                }
            }
        },

        flush: function () {
            const me = this;

            const requests = _.values(me.pendingRequestsByUUID);
            me.inflightRequestsByUUID = _.extend({}, me.inflightRequestsByUUID, me.pendingRequestsByUUID);
            me.pendingRequestsByUUID = {};

            if (!_.isEmpty(requests)) {
                const requestsByRequestGroup = _.groupBy(requests, '___requestGroup');

                _.each(requestsByRequestGroup, (requests) => {
                    // NOTE: intentionally not awaiting here, as we want to fire off all the requests in parallel
                    me._requestMetricsForGroupAsync(requests);
                });

            } else {
                me._notifyWaitingCallers();
            }
        },

        invalidateCaches: function (reissueInflightRequests) {
            const me = this;

            me.resetInvalidationId();
            me.cachedResultsByUUID = {};

            if (reissueInflightRequests) {

                // If we're going to reissue the requests, we need to reissue requests for all the inflightRequestsByUUID as well as
                // all of the requests that the waiting callers initially asked for (as some of those initial requests may have been
                // in me.cachedResultsByUUID at request time but are obviously no longer there).
                me.pendingRequestsByUUID = _.reduce(me.waitingCallers, (memo, waitingCaller) => {
                    _.each(waitingCaller.canonicalized, (request) => {
                        const uuid = RivalIQ.metrics.new.MetricRequest.uniqueKeyForMetricRequest(request);
                        memo[uuid] = request;
                    });

                    return memo;
                }, _.extend({}, me.inflightRequestsByUUID));

                me._debouncedFlush();
            }
        }
    });

    client.CacheLayer = new CacheLayer();
});
